import { useState, useEffect } from 'react';
import { Bell, BellOff, LogOut, Loader2, VolumeX, Volume2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/context/AuthContext';
import { useFamily } from '@/context/FamilyContext';
import { supabase } from '@/lib/supabase';
import {
  pushSupported, subscribeToPush, unsubscribeFromPush,
  getCurrentSubscription, isIOS, isStandaloneMode
} from '@/lib/push';
import { IOSPushHint } from '@/components/IOSPushHint';
import { TopBar } from '@/components/TopBar';
import type { Reminder } from '@/types';

export function Settings() {
  const { user, signOut } = useAuth();
  const { family } = useFamily();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [mutedIds, setMutedIds] = useState<Set<string>>(new Set());
  const [muteLoading, setMuteLoading] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const sub = await getCurrentSubscription();
      setPushEnabled(!!sub);
      setPushLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!family || !user) return;
    (async () => {
      const { data: rems } = await supabase.from('reminders').select('*').eq('family_id', family.id).eq('is_active', true).order('title');
      setReminders((rems ?? []) as Reminder[]);
      const { data: muted } = await supabase.from('muted_reminders').select('reminder_id').eq('user_id', user.id);
      setMutedIds(new Set((muted ?? []).map((m: { reminder_id: string }) => m.reminder_id)));
    })();
  }, [family, user]);

  const togglePush = async () => {
    setPushLoading(true);
    if (pushEnabled) {
      await unsubscribeFromPush();
      setPushEnabled(false);
      toast.success('Push notifications disabled');
    } else {
      const { ok, error } = await subscribeToPush();
      if (ok) {
        setPushEnabled(true);
        toast.success('Push notifications enabled!');
      } else {
        toast.error(error ?? 'Failed to enable push notifications');
      }
    }
    setPushLoading(false);
  };

  const toggleMute = async (reminderId: string) => {
    if (!user) return;
    setMuteLoading(reminderId);
    if (mutedIds.has(reminderId)) {
      await supabase.from('muted_reminders').delete().eq('user_id', user.id).eq('reminder_id', reminderId);
      setMutedIds((prev) => { const s = new Set(prev); s.delete(reminderId); return s; });
    } else {
      await supabase.from('muted_reminders').insert({ user_id: user.id, reminder_id: reminderId });
      setMutedIds((prev) => new Set(prev).add(reminderId));
    }
    setMuteLoading(null);
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
  };

  const needsIOSHint = isIOS() && !isStandaloneMode() && !pushEnabled;

  return (
    <div>
      <TopBar title="Settings" />
      <div className="px-4 py-4 space-y-5">

        {/* Account */}
        <Section title="Account">
          <div className="flex items-center gap-3 p-3">
            <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
              <span className="text-brand-700 font-semibold">{(user?.email?.[0] ?? '?').toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{user?.user_metadata?.full_name ?? 'You'}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Divider />
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center gap-3 px-3 py-3 text-red-600 hover:bg-red-50 rounded-b-xl transition-colors"
          >
            {signingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
            <span className="text-sm font-medium">Sign out</span>
          </button>
        </Section>

        {/* Push notifications */}
        <Section title="Notifications">
          {needsIOSHint && <IOSPushHint />}
          {!pushSupported() && (
            <p className="px-3 py-3 text-sm text-gray-500">Push notifications are not supported in this browser.</p>
          )}
          {pushSupported() && (
            <div className="flex items-center justify-between px-3 py-3">
              <div className="flex items-center gap-3">
                {pushEnabled ? <Bell className="w-5 h-5 text-brand-600" /> : <BellOff className="w-5 h-5 text-gray-400" />}
                <div>
                  <p className="text-sm font-medium text-gray-900">Push notifications</p>
                  <p className="text-xs text-gray-400">{pushEnabled ? 'You will receive reminders' : 'Enable to receive reminders'}</p>
                </div>
              </div>
              <button
                onClick={togglePush}
                disabled={pushLoading}
                className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${pushEnabled ? 'bg-brand-600' : 'bg-gray-200'} disabled:opacity-50`}
              >
                {pushLoading && <Loader2 className="absolute inset-0 m-auto w-3.5 h-3.5 animate-spin text-white" />}
                {!pushLoading && (
                  <span className={`block w-5 h-5 rounded-full bg-white shadow-md absolute top-0.5 transition-transform ${pushEnabled ? 'translate-x-5.5' : 'translate-x-0.5'}`} style={{ transform: pushEnabled ? 'translateX(22px)' : 'translateX(2px)' }} />
                )}
              </button>
            </div>
          )}
        </Section>

        {/* Mute individual reminders */}
        {reminders.length > 0 && (
          <Section title="Mute specific reminders">
            {reminders.map((r, i) => (
              <div key={r.id}>
                {i > 0 && <Divider />}
                <div className="flex items-center justify-between px-3 py-2.5">
                  <p className="text-sm text-gray-800 flex-1 truncate">{r.title}</p>
                  <button
                    onClick={() => toggleMute(r.id)}
                    disabled={muteLoading === r.id}
                    className="ml-3 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {muteLoading === r.id
                      ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      : mutedIds.has(r.id)
                      ? <VolumeX className="w-4 h-4 text-red-500" />
                      : <Volume2 className="w-4 h-4 text-gray-400" />
                    }
                  </button>
                </div>
              </div>
            ))}
          </Section>
        )}

        <p className="text-center text-xs text-gray-300 pb-2">Family Assets & Reminders v1.0</p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-gray-50 mx-3" />;
}
